import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage } from '../../api/client';
import { disablePush, enablePush, pushState, testPush, type PushState } from '../../push';

const KEY = ['push-state'] as const;

const EXPLAIN: Readonly<Record<PushState, string>> = {
  unsupported: 'This browser cannot show notifications from Couch. On an iPhone: add Couch to the home screen first and open it from there.',
  denied: 'Notifications are blocked for Couch in this browser’s settings. Allow them there, then come back.',
  off: 'Get a notification on this device when an image is done or a run fails.',
  on: 'This device is notified when an image is done or a run fails.',
};

/** Opt-in per device for "your image is done" (doc 28). */
export function NotificationsPanel() {
  const queryClient = useQueryClient();
  const state = useQuery({ queryKey: KEY, queryFn: pushState });
  const set = (next: PushState) => { queryClient.setQueryData(KEY, next); };
  const toggle = useMutation({ mutationFn: () => (state.data === 'on' ? disablePush() : enablePush()), onSuccess: set });
  const test = useMutation({ mutationFn: testPush });
  const current = state.data;
  return (
    <section className="panel" aria-labelledby="push-title">
      <h2 id="push-title">Notifications</h2>
      {current && <p className="muted">{EXPLAIN[current]}</p>}
      {(current === 'off' || current === 'on') && (
        <div className="machine-actions">
          <button type="button" className={current === 'on' ? 'button-quiet' : undefined} disabled={toggle.isPending} onClick={() => { toggle.mutate(); }}>
            {current === 'on' ? 'Turn off on this device' : 'Notify this device'}
          </button>
          {current === 'on' && <button type="button" className="button-quiet" disabled={test.isPending} onClick={() => { test.mutate(); }}>Send a test</button>}
        </div>
      )}
      {test.isSuccess && <p role="status" className="success">Test sent — it should appear in a moment.</p>}
      {[state.error, toggle.error, test.error].filter(Boolean).map((e, n) => <p key={n} role="alert" className="form-error">{errorMessage(e)}</p>)}
    </section>
  );
}
